import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, CardContent, Button, Typography, LinearProgress,
  Paper, useTheme, Drawer, List, ListItem,
  ListItemButton, ListItemText
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import FlipIcon from '@mui/icons-material/Flip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import SingleVerseDialog from './SingleVerseDialog';
import { getBibles, getBooks, getChapters, getVerses, getVerseText } from '../services/bibleApi';
import { getSettings, addAttempt, Cache, saveSettings, chunkContent, fallbackChunkText } from '../utils/storage';
import { makeBookAbbrMap, bibleBooks, getBookNameById } from '../utils/books';

// Helper function to strip HTML tags from content and remove leading verse number
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  let text = tmp.textContent || tmp.innerText || '';
  text = text.replace(/^\d+\s*/, '');
  return text.trim();
};

// Helper function to normalize verse reference to full book name
const normalizeVerseReference = (ref) => {
  const parts = ref.trim().split(/\s+/);
  if (parts.length < 2) return ref;
  const firstPart = parts[0];
  if (firstPart.length <= 3 && firstPart === firstPart.toUpperCase() && firstPart !== firstPart.toLowerCase()) {
    const bookName = getBookNameById(firstPart);
    if (bookName) {
      return ref.replace(firstPart, bookName);
    }
  }
  return ref;
};

const Memorize = () => {
  const theme = useTheme();
  const { language, selectedBibleId, selectedVerseReference } = getSettings();

  // Map browser language to ISO 639-3 codes used in the database
  const languageMap = {
    'en': 'eng',
    'es': 'spa',
    'pt': 'por',
    'fr': 'fra',
    'de': 'deu',
    'it': 'ita',
    'ru': 'rus',
    'zh': 'cmn',
    'ar': 'arb',
    'ja': 'jpn',
    'ko': 'kor'
  };

  // Speech recognition language map (ISO 639-1 to full locale)
  const speechLangMap = {
    'eng': 'en-US',
    'spa': 'es-ES',
    'por': 'pt-BR',
    'fra': 'fr-FR',
    'deu': 'de-DE',
    'ita': 'it-IT',
    'rus': 'ru-RU',
    'cmn': 'zh-CN',
    'arb': 'ar-SA',
    'jpn': 'ja-JP',
    'kor': 'ko-KR',
    'en': 'en-US',
    'es': 'es-ES',
    'pt': 'pt-BR',
    'fr': 'fr-FR',
    'de': 'de-DE',
    'it': 'it-IT',
    'ru': 'ru-RU',
    'zh': 'zh-CN',
    'ar': 'ar-SA',
    'ja': 'ja-JP',
    'ko': 'ko-KR'
  };

  const browserLang = navigator.language.split('-')[0] || 'en';
  const currentLang = languageMap[language || browserLang] || language || browserLang;
  const speechLang = speechLangMap[currentLang] || 'en-US';

  // Book abbreviation map
  const bookAbbrMap = makeBookAbbrMap();

  // States
  const [bibles, setBibles] = useState([]);
  const [selectedBible, setSelectedBible] = useState(selectedBibleId || '');
  const [verseText, setVerseText] = useState('');
  const [verseReference, setVerseReference] = useState(normalizeVerseReference(selectedVerseReference || 'Genesis 1:1'));

  // Version Picker drawer state
  const [isVersionDrawerOpen, setIsVersionDrawerOpen] = useState(false);

  // Verse Picker states
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState('GEN');
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [selectedVerse, setSelectedVerse] = useState(1);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [availableVerses, setAvailableVerses] = useState([]);

  // Books state for picker
  const [books, setBooks] = useState([]);

  const openPicker = () => {
    setIsPickerOpen(true);
  };

  // Memorization states
  const [flipped, setFlipped] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [mastery, setMastery] = useState(0);
  const [chunks, setChunks] = useState([]);
  const [masteredChunks, setMasteredChunks] = useState([]);
  const [currentChunkText, setCurrentChunkText] = useState('');
  const [currentDisplayLevel, setCurrentDisplayLevel] = useState(null); // Temporary display level for current verse

  const recognitionRef = useRef(null);
  const verseLoadedRef = useRef(false);
  const pauseTimeoutRef = useRef(null); // For detecting speech pauses

  // Get patent settings
  const settings = getSettings();
  const patentSettings = settings.patentSettings;



  const loadVerseFromReference = useCallback(async () => {
    if (!selectedBible || !verseReference) return;

    console.log('🔄 loadVerseFromReference called with:', verseReference, selectedBible);

    const parts = verseReference.trim().split(/\s+/);
    if (parts.length < 2) {
      setVerseText('Invalid reference format. Use Book Chapter:Verse');
      return;
    }

    // Try to parse book name, handling multi-word book names
    let bookPart = parts[0].toLowerCase();
    let refIndex = 1;

    // Check if next part looks like chapter:verse, else combine with book part
    while (refIndex < parts.length && parts[refIndex] && !parts[refIndex].includes(':')) {
      bookPart += ' ' + parts[refIndex].toLowerCase();
      refIndex++;
    }

    if (refIndex >= parts.length || !parts[refIndex].includes(':')) {
      setVerseText('Invalid reference format. Use Book Chapter:Verse');
      return;
    }

    let abbr = bookAbbrMap[bookPart];
    if (!abbr) {
      // Check if it's already an abbreviation
      const upperBookPart = bookPart.toUpperCase();
      const foundBook = bibleBooks.find(b => b.id === upperBookPart);
      if (foundBook) {
        abbr = foundBook.id;
      } else {
        setVerseText(`Unknown book: ${bookPart}`);
        return;
      }
    }

    const chapterVerse = parts[refIndex];
    const [chapter, verse] = chapterVerse.split(':');

    if (!chapter || !verse) {
      setVerseText('Invalid chapter:verse format');
      return;
    }

    const verseId = `${abbr}.${chapter}.${verse}`;

    try {
      console.log('📡 Loading verse:', verseId);
      const verseText = await getVerseText(selectedBible, verseId);
      console.log('📥 Verse received:', verseText ? verseText.substring(0, 50) : 'empty');
      setVerseText(verseText || 'Verse not found');
      if (!verseLoadedRef.current) {
        setFlipped(false);
        verseLoadedRef.current = true;
      }
      // Clear transcript when loading new verse
      setTranscript('');
      setMastery(0);
    } catch (error) {
      console.error('Error loading verse:', error);
      setVerseText('Verse not found');
    }
  }, [selectedBible, verseReference, bookAbbrMap]);

  const loadChaptersForBook = useCallback(async () => {
    try {
      const chapters = await getChapters(selectedBible, selectedBookId);
      setAvailableChapters(chapters);
      // Reset to first chapter if current selection is invalid
      if (chapters.length > 0 && (!selectedChapter || selectedChapter > chapters.length)) {
        setSelectedChapter(1);
      }
    } catch (error) {
      console.error('Error loading chapters:', error);
      // Fallback to mock
      setAvailableChapters(Array.from({ length: selectedBookId === 'PSA' ? 150 : selectedBookId === 'MAT' ? 28 : 50 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  }, [selectedBible, selectedBookId, selectedChapter]);

  const loadVersesForChapter = useCallback(async () => {
    try {
      const verses = await getVerses(selectedBible, `${selectedBookId}.${selectedChapter}`);
      setAvailableVerses(verses);
      // Reset to first verse if current selection is invalid
      if (verses.length > 0 && (!selectedVerse || selectedVerse > verses.length)) {
        setSelectedVerse(1);
      }
    } catch (error) {
      console.error('Error loading verses:', error);
      // Fallback to mock
      setAvailableVerses(Array.from({ length: 30 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  }, [selectedBible, selectedBookId, selectedChapter, selectedVerse]);

  const loadBooks = useCallback(async () => {
    console.log('Loading books for bible:', selectedBible);
    try {
      const bookList = await getBooks(selectedBible);
      console.log('Loaded books:', bookList);
      setBooks(bookList);
    } catch (error) {
      console.error('Error loading books:', error);
      setBooks([]);
    }
  }, [selectedBible]);

  // Load Bibles on component mount
  useEffect(() => {
    const loadBibles = async () => {
      console.log('Loading bibles with language:', currentLang);
      const cacheKey = `bibles_${currentLang}`;
      const cached = Cache.get(cacheKey);
      if (cached) {
        console.log('Using cached bibles data:', cached);
        setBibles(cached);
        // Set default bible if no saved selection
        if (!selectedBible && cached.length > 0) {
          setSelectedBible(cached[0].id);
          if (!selectedBibleId) {
            saveSettings({ ...getSettings(), selectedBibleId: cached[0].id });
          }
        }
        return;
      }
      try {
        console.log('Fetching bibles from API...');
        const data = await getBibles(currentLang);
        console.log('Fetched bibles data:', data);
        setBibles(data);
        Cache.set(cacheKey, data);
        if (!selectedBible && data.length > 0) {
          setSelectedBible(data[0].id);
          if (!selectedBibleId) {
            saveSettings({ ...getSettings(), selectedBibleId: data[0].id });
          }
        }
      } catch (error) {
        console.error('Failed to load bibles:', error.message);
      }
    };

    loadBibles();
  }, [currentLang, selectedBible, selectedBibleId]);



  // Load verse when reference changes
  useEffect(() => {
    if (verseReference && selectedBible) {
      loadVerseFromReference();
    }
  }, [verseReference, selectedBible, loadVerseFromReference]);

  // Update display text when verse or display level changes
  useEffect(() => {
    if (verseText) {
      const cleanText = stripHtml(verseText);
      const displayLevel = currentDisplayLevel || patentSettings.verseDisplayLevel || 'verse';

      // For chunking (progressive learning), use current display level
      if (displayLevel !== 'verse') {
        let verseChunks = chunkContent(cleanText, displayLevel);
        if (verseChunks.length === 1) {
          // If no punctuation found, apply fallback word-based splitting
          const fallbackText = chunkContent(cleanText, 'words').join(' ');
          verseChunks = [fallbackChunkText(fallbackText, displayLevel === 'sentence' ? 2 : 3)];
        }
        setChunks(verseChunks);
        setMasteredChunks([]);
        setCurrentChunkText(verseChunks[0]);
        // Reset memorization state when display level changes
        setFlipped(false);
        setTranscript('');
        setMastery(0);
      } else {
        setChunks([cleanText]);
        setMasteredChunks([]);
        setCurrentChunkText(cleanText);
        setFlipped(false);
        setTranscript('');
        setMastery(0);
      }
    }
  }, [verseText, patentSettings.verseDisplayLevel, currentDisplayLevel]);

  // Load chapters when book changes
  useEffect(() => {
    if (selectedBookId && selectedBible) {
      loadChaptersForBook();
    }
  }, [selectedBookId, selectedBible, loadChaptersForBook]);

  // Load verses when chapter changes
  useEffect(() => {
    if (selectedBookId && selectedChapter && selectedBible) {
      loadVersesForChapter();
    }
  }, [selectedBookId, selectedChapter, selectedBible, loadVersesForChapter]);

  // Load books when bible changes
  useEffect(() => {
    if (selectedBible) {
      loadBooks();
    }
  }, [selectedBible, loadBooks]);


  const handleBookSelect = (bookId) => {
    setSelectedBookId(bookId);
    setSelectedChapter(1);
    setSelectedVerse(1);
  };

  const handleChapterSelect = (chapterNum) => {
    setSelectedChapter(chapterNum);
    setSelectedVerse(1);
  };

  const handleVerseSelect = (verseNum) => {
    setSelectedVerse(verseNum);
  };

  const handleConfirmPicker = () => {
    const bookName = getBookNameById(selectedBookId);
    const reference = `${bookName} ${selectedChapter}:${selectedVerse}`;
    setVerseReference(reference);
    setIsPickerOpen(false);
    saveSettings({ ...getSettings(), selectedVerseReference: reference });
  };

  const handleVersionSelect = (bible) => {
    setSelectedBible(bible.id);
    setIsVersionDrawerOpen(false);
    saveSettings({ ...getSettings(), selectedBibleId: bible.id });
  };

  const handleDisplayLevelChange = (direction) => {
    const levels = ['verse', 'sentence', 'phrase'];
    const currentLevel = currentDisplayLevel || patentSettings.verseDisplayLevel || 'verse';
    const currentIndex = levels.indexOf(currentLevel);

    let newIndex;
    if (direction === 'less') {
      // Show less text (move to smaller chunks)
      newIndex = Math.min(currentIndex + 1, levels.length - 1);
    } else {
      // Show more text (move towards full verse)
      newIndex = Math.max(currentIndex - 1, 0);
    }

    if (newIndex !== currentIndex) {
      console.log('Changing display level from', currentLevel, 'to', levels[newIndex]);
      setCurrentDisplayLevel(levels[newIndex]);
    } else {
      console.log('No change needed, already at limit');
    }
  };

  const getCurrentDisplayLevel = () => {
    return currentDisplayLevel || patentSettings.verseDisplayLevel || 'verse';
  };

  const getFlipButtonText = () => {
    if (!flipped) return 'Say It!';

    const displayLevel = getCurrentDisplayLevel();
    if (displayLevel === 'verse') return 'Show Verse';

    // For chunked levels, check if there are more chunks remaining
    const currentChunkIndex = chunks.findIndex(chunk => chunk === currentChunkText);
    const hasMoreChunks = currentChunkIndex < chunks.length - 1;

    if (hasMoreChunks) {
      // Return specific text based on the display level
      switch (displayLevel) {
        case 'phrase':
          return 'Show Phrase';
        case 'sentence':
          return 'Show Sentence';
        default:
          return 'Show Chunk';
      }
    } else {
      return 'Verse Complete';
    }
  };

  const handleFlip = () => {
    setFlipped(!flipped);
  };

  const toggleListening = async () => {
    if (isListening) {
      console.log('Manually stopping speech recognition');
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
    } else {
      console.log('Manually starting speech recognition');
      await startListening();
    }
  };

  const startListening = useCallback(async () => {
    try {
      // Check for HTTPS requirement
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        console.error('Speech recognition requires HTTPS or localhost');
        alert('Speech recognition requires a secure connection (HTTPS). Please access this site over HTTPS.');
        return;
      }

      // Request microphone permission if not already granted
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('Microphone permission granted');
        stream.getTracks().forEach(track => track.stop());
      } catch (permissionError) {
        console.error('Microphone permission denied:', permissionError);
        alert('Microphone access is required for speech recognition. Please allow microphone access and try again.');
        return;
      }

      if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
        console.warn('Speech recognition not supported in this browser.');
        alert('Speech recognition is not supported in this browser. Please use a modern browser like Chrome or Edge.');
        return;
      }

      // Clean up any existing recognition instance
      if (recognitionRef.current) {
        console.log('Stopping previous recognition instance');
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      console.log('Created new SpeechRecognition instance');
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = speechLang;
      console.log('Recognition settings: continuous=true, interimResults=true, lang=' + speechLang);

        recognitionRef.current.onresult = (event) => {
          console.log('Speech recognition result event received, result count:', event.results.length);

          // Clear any existing pause timeout
          if (pauseTimeoutRef.current) {
            clearTimeout(pauseTimeoutRef.current);
          }

          let currentTranscript = '';
          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            console.log('Result', i, ': final=' + result.isFinal + ', confidence=' + (result[0]?.confidence || 'N/A') + ', transcript="' + (result[0]?.transcript || '') + '"');
            // Include both interim and final results for real-time display
            currentTranscript += result[0].transcript;
          }

          // Update transcript state
          if (currentTranscript.trim()) {
            setTranscript(currentTranscript);
          }

          // Check for real-time mastery if accuracy reaches threshold
          if (currentTranscript.trim() && currentChunkText) {
            const currentAccuracy = calculateAccuracy(currentChunkText, currentTranscript);

            // For chunked mode, stop listening if accuracy reaches 100%
            const displayLevel = currentDisplayLevel || patentSettings.verseDisplayLevel || 'verse';
            if (displayLevel !== 'verse' && currentAccuracy === 100) {
              console.log(`Perfect accuracy (100%) achieved for chunk - stopping listening`);
              if (pauseTimeoutRef.current) {
                clearTimeout(pauseTimeoutRef.current);
              }
              if (recognitionRef.current) {
                recognitionRef.current.stop();
              }
              return; // Exit early since we're stopping
            }

            if (currentAccuracy >= patentSettings.accuracyThreshold && mastery < 1) {
              console.log(`Real-time accuracy ${currentAccuracy}% meets threshold - updating mastery`);
              setMastery(1); // Set at least 1 mastery star when threshold is met
            }
          }

          // Set a 3-second timeout for auto-stop on speech pause
          pauseTimeoutRef.current = setTimeout(() => {
            console.log('Speech pause detected (3s) - stopping recognition');
            // Stop listening (mastery evaluation will be handled by useEffect)
            if (recognitionRef.current) {
              recognitionRef.current.stop();
            }
          }, 3000);
        };

      recognitionRef.current.onstart = () => {
        console.log('Speech recognition started successfully');
        setIsListening(true);
      };

      recognitionRef.current.onend = () => {
        console.log('Speech recognition ended');
        setIsListening(false);

        // Clear any pending pause timeout
        if (pauseTimeoutRef.current) {
          clearTimeout(pauseTimeoutRef.current);
          pauseTimeoutRef.current = null;
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error, event);
        setIsListening(false);
      };

      recognitionRef.current.onsoundstart = () => {
        console.log('Sound detected, speech recognition active');
      };

      recognitionRef.current.onsoundend = () => {
        console.log('Sound ended, waiting for more input');
      };

      recognitionRef.current.onaudiostart = () => {
        console.log('Audio capture started');
      };

      recognitionRef.current.onaudioend = () => {
        console.log('Audio capture ended');
      };

      console.log('Starting speech recognition...');
      recognitionRef.current.start();

    } catch (error) {
      console.error('Speech recognition initialization failed:', error);
      alert('Failed to initialize speech recognition: ' + error.message);
      setIsListening(false);
    }
  }, [speechLang]);

  // Start speech recognition when card flips to flipped state
  useEffect(() => {
    if (!flipped) return;
    startListening();
  }, [flipped, speechLang, startListening]);

  // Evaluate mastery when listening stops (either manually or auto-stop)
  useEffect(() => {
    if (isListening || !transcript || !verseText) return;

    console.log('Evaluating mastery on speech recognition stop');

    const finalAccuracy = calculateAccuracy(verseText, transcript);
    addAttempt({ verseId: verseReference, originalText: verseText, transcribedText: transcript, accuracy: finalAccuracy });

    if (finalAccuracy >= patentSettings.accuracyThreshold) {
      console.log(`Final accuracy ${finalAccuracy}% meets threshold - awarding mastery`);
      setMastery(prev => (prev < 5 ? prev + 1 : prev));
      setFlipped(false); // Progress to next chunk
      setTranscript(''); // Clear transcript
    } else {
      console.log(`Final accuracy ${finalAccuracy}% below threshold - no mastery awarded`);
    }
  }, [isListening, transcript, verseText, patentSettings.accuracyThreshold, verseReference]);

  // Stop speech recognition when card flips back
  useEffect(() => {
    if (flipped) return;
    
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, [flipped]);

  // Record attempt and handle progression when flipped becomes false (card flips back)
  useEffect(() => {
    if (flipped || !transcript || !currentChunkText) return;

    // Calculate accuracy based on current chunk being practiced
    const accuracy = calculateChunkAccuracy(currentChunkText, transcript);
    addAttempt({ verseId: verseReference, originalText: currentChunkText, transcribedText: transcript, accuracy });

    // Handle chunk progression based on patent settings
    const accuracyThreshold = patentSettings.accuracyThreshold;
    const displayLevel = currentDisplayLevel || patentSettings.verseDisplayLevel || 'verse';
    const isChunked = displayLevel !== 'verse'; // Force isolated mode for chunked levels

    if (accuracy >= accuracyThreshold) {
      // Chunk mastered - advance to next chunk based on compilation type
      const currentChunkIndex = chunks.findIndex(chunk => chunk === currentChunkText);

      if (isChunked || patentSettings.progressCompilationType === 'progressive') {
        if (currentChunkIndex < chunks.length - 1) {
          // Always use isolated mode for chunked levels: move to next chunk individually
          setCurrentChunkText(chunks[currentChunkIndex + 1]);
        } else {
          // Verse completed
          setMastery(prev => (prev < 5 ? prev + 1 : prev)); // Cap at 5
        }
      } else {
        // Non-chunked level with progressive compilation: add next chunk to current text
        const newMasteredChunks = [...masteredChunks, currentChunkIndex];
        const combinedText = chunks.slice(0, newMasteredChunks.length + 1).join(' ');
        setCurrentChunkText(combinedText);
        setMasteredChunks(newMasteredChunks);
      }

      // Reset transcript for next chunk
      setTranscript('');
      setFlipped(false);
    } else {
      // Accuracy below threshold - track repetition attempts
      setMastery(prev => (prev > 0 ? prev - 1 : 0)); // Decrease mastery on failure
      // Transcript remains visible for user to try again
    }
  }, [flipped, transcript, currentChunkText, patentSettings, chunks, masteredChunks, verseReference, currentDisplayLevel]);

  const calculateChunkAccuracy = (chunkText, transcribed) => {
    const chunkWords = chunkText.replace(/[^\w\s]/g, '').toLowerCase().split(/\s+/).filter(word => word.length > 0);
    const transWords = transcribed.replace(/[^\w\s]/g, '').toLowerCase().split(/\s+/).filter(word => word.length > 0);

    const chunkSet = new Set(chunkWords);
    let matchedWords = 0;

    const usedWords = new Set();
    transWords.forEach(word => {
      if (chunkSet.has(word) && !usedWords.has(word)) {
        matchedWords++;
        usedWords.add(word);
      }
    });

    return Math.round((matchedWords / chunkWords.length) * 100);
  };

  const calculateAccuracy = (original, transcribed) => {
    const cleanOriginal = stripHtml(original);
    const origWords = cleanOriginal.replace(/[^\w\s]/g, '').toLowerCase().split(/\s+/).filter(word => word.length > 0);
    const transWords = transcribed.replace(/[^\w\s]/g, '').toLowerCase().split(/\s+/).filter(word => word.length > 0);

    const origSet = new Set(origWords);
    let matchedWords = 0;

    const usedWords = new Set();
    transWords.forEach(word => {
      if (origSet.has(word) && !usedWords.has(word)) {
        matchedWords++;
        usedWords.add(word);
      }
    });

    return Math.round((matchedWords / origWords.length) * 100);
  };

  const renderTranscribedText = () => {
    if (!verseText || !transcript) return transcript;
    const cleanVerseText = stripHtml(verseText);
    const origWords = cleanVerseText.replace(/[^\w\s]/g, '').toLowerCase().split(/\s+/);
    const transWords = transcript.replace(/[^\w\s]/g, '').toLowerCase().split(/\s+/);
    return transWords.map((word, index) => {
      const isCorrect = origWords.includes(word.toLowerCase());
      return (
        <span key={index} style={{
          color: isCorrect ? theme.palette.success.main : theme.palette.error.main,
          fontWeight: isCorrect ? 'normal' : 'bold',
        }}>
          {word}{' '}
        </span>
      );
    });
  };

  // Calculate accuracy for the currently displayed text (fragment or full verse)
  const accuracy = verseText && transcript ? calculateAccuracy(currentChunkText, transcript) : 0;
  const selectedBibleObj = bibles.find(b => b.id === selectedBible);

  // Removed - now handled by pause-detection and final evaluation

  return (
    <Box sx={{
      height: { xs: '80vh', sm: '70vh' },
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Version and Verse Pickers Container */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        {/* Verse Reference Picker - Left */}
        <Box sx={{ flex: 1, position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          {/* Notched Border */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: `1px solid ${theme.palette.outline.light}`,
              borderRadius: 2,
              '&:hover': {
                borderColor: theme.palette.primary.main,
              }
            }}
          />
          {/* Label */}
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              top: -12,
              left: 12,
              backgroundColor: theme.palette.background.paper,
              px: 0.5,
              fontSize: '0.875rem',
              color: theme.palette.text.secondary,
              fontWeight: 500,
              zIndex: 1
            }}
          >
            Verse Reference
          </Typography>
          {/* Button */}
          <Button
            onClick={openPicker}
            variant="text"
            endIcon={<ExpandMoreIcon />}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: '1rem',
              padding: '8px 14px',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {verseReference}
            </Typography>
          </Button>
        </Box>

        {/* Version Picker - Right */}
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          {/* Notched Border */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: `1px solid ${theme.palette.outline.light}`,
              borderRadius: 2,
              '&:hover': {
                borderColor: theme.palette.primary.main,
              }
            }}
          />
          {/* Label */}
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              top: -12,
              left: 12,
              backgroundColor: theme.palette.background.paper,
              px: 0.5,
              fontSize: '0.875rem',
              color: theme.palette.text.secondary,
              fontWeight: 500,
              zIndex: 1
            }}
          >
            Version
          </Typography>
          {/* Button */}
          <Button
            onClick={() => setIsVersionDrawerOpen(!isVersionDrawerOpen)}
            variant="text"
            endIcon={isVersionDrawerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              px: 1,
              py: 0.5,
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: 'auto',
              borderRadius: 2,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedBibleObj ? selectedBibleObj.name : 'Loading...'}
            </Typography>
          </Button>

        </Box>
      </Box>

      {/* Verse Card */}
      <Card
        sx={{
          background: flipped
            ? `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
          color: flipped ? theme.palette.primary.contrastText : theme.palette.text.primary,
          transition: 'all 0.5s ease',
          flexGrow: 1,
          borderRadius: theme.shape.borderRadius * 2,
          border: `1px solid ${theme.palette.outline.light}`,
          boxShadow: theme.shadows[3],
          display: 'flex',
          flexDirection: 'column',
          mb: 2
        }}
      >
        <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {!flipped && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDisplayLevelChange('more')}
                startIcon={<ZoomInIcon />}
                disabled={getCurrentDisplayLevel() === 'verse'}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderColor: theme.palette.outline.main,
                  color: theme.palette.text.secondary
                }}
              >
                More
              </Button>
              <Typography variant="caption" sx={{ alignSelf: 'center', mx: 1, color: theme.palette.text.secondary }}>
                {getCurrentDisplayLevel().charAt(0).toUpperCase() + getCurrentDisplayLevel().slice(1)}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDisplayLevelChange('less')}
                startIcon={<ZoomOutIcon />}
                disabled={getCurrentDisplayLevel() === 'phrase'}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderColor: theme.palette.outline.main,
                  color: theme.palette.text.secondary
                }}
              >
                Less
              </Button>
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }}>
            {!flipped ? (
              <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontStyle: 'italic',
                    lineHeight: 1.8,
                    fontSize: { xs: '1.8rem', sm: '2.5rem' },
                    color: 'inherit',
                    fontWeight: 400,
                    whiteSpace: 'pre-line'
                  }}
                >
                  "{currentChunkText}"
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 3,
                    color: flipped ? 'rgba(255,255,255,0.8)' : theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: '1.2rem'
                  }}
                >
                  — {verseReference}
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                {/* Show Chunk Button - 1/5 height */}
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 0
                }}>
                  <Button
                    variant="outlined"
                    onClick={handleFlip}
                    startIcon={<FlipIcon />}
                    sx={{
                      backgroundColor: flipped ? 'rgba(255,255,255,0.1)' : theme.palette.background.surfaceContainer,
                      color: flipped ? 'rgba(255,255,255,0.9)' : theme.palette.text.primary,
                      borderColor: flipped ? 'rgba(255,255,255,0.3)' : theme.palette.outline.main,
                      px: 3,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: flipped ? 'rgba(255,255,255,0.2)' : theme.palette.action.hover,
                        borderColor: flipped ? 'rgba(255,255,255,0.5)' : theme.palette.primary.main,
                      },
                    }}
                  >
                    {getFlipButtonText()}
                  </Button>
                </Box>

                {/* Transcription Paper - 1/5 height */}
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 0,
                  px: 2
                }}>
                  <Paper
                    sx={{
                      width: '100%',
                      p: 2,
                      backgroundColor: flipped ? 'rgba(255,255,255,0.15)' : theme.palette.background.surfaceContainerHighest,
                      backdropFilter: 'blur(10px)',
                      borderRadius: 1,
                      minHeight: '100%',
                      maxHeight: '100%',
                      overflow: 'auto',
                      border: `1px solid ${flipped ? 'rgba(255,255,255,0.2)' : theme.palette.outline.light}`,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.4,
                        color: flipped ? 'rgba(255,255,255,0.9)' : theme.palette.text.primary,
                        fontSize: '0.95rem',
                        width: '100%',
                        textAlign: 'center'
                      }}
                    >
                      {renderTranscribedText() || <em style={{ opacity: 0.7 }}>Speak the verse...</em>}
                    </Typography>
                  </Paper>
                </Box>

                {/* Accuracy Display - 1/5 height */}
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: 0,
                  px: 2
                }}>
                  {/* Accuracy label and % value overlaid on progress bar */}
                  <Box sx={{ position: 'relative', height: 32, display: 'flex', alignItems: 'center' }}>
                    {/* Accuracy label - stacked vertically above progress bar */}
                    <Typography
                      variant="body2"
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: -15,
                        color: flipped ? 'rgba(255,255,255,0.9)' : theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        zIndex: 2,
                        fontWeight: 500,
                        lineHeight: 1.2
                      }}
                    >
                      Accuracy
                    </Typography>
                    {/* Accuracy % value - centered in progress bar */}
                    <Typography
                      variant="body2"
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: flipped ? 'rgba(255,255,255,0.9)' : theme.palette.text.primary,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        zIndex: 2,
                        lineHeight: 1.2,
                        textShadow: flipped ? '1px 1px 2px rgba(0,0,0,0.5)' : '1px 1px 2px rgba(255,255,255,0.8)'
                      }}
                    >
                      {accuracy}%
                    </Typography>
                    {/* Progress bar */}
                    <LinearProgress
                      variant="determinate"
                      value={accuracy}
                      sx={{
                        height: 20,
                        width: '100%',
                        borderRadius: 2,
                        backgroundColor: flipped ? 'rgba(255,255,255,0.3)' : theme.palette.action.disabledBackground,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 2,
                          background: accuracy >= 80
                            ? `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
                            : accuracy >= 50
                              ? `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`
                              : `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.light})`,
                        },
                      }}
                    />
                    {/* Threshold marker overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${Math.min(patentSettings.accuracyThreshold, 100)}%`,
                        top: 4,
                        width: 4,
                        height: 25,
                        backgroundColor: flipped ? 'white' : theme.palette.text.primary,
                        transform: 'translateX(-50%)',
                        zIndex: 3,
                        boxShadow: '0 0 8px rgba(0,0,0,0.6)',
                        borderRadius: 1,
                      }}
                      title={`Accuracy threshold: ${patentSettings.accuracyThreshold}%`}
                    />
                    {/* Threshold label overlay */}
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: `${Math.min(patentSettings.accuracyThreshold, 100)}%`,
                        top: 30,
                        transform: 'translateX(-50%)',
                        color: flipped ? 'rgba(255,255,255,0.9)' : theme.palette.text.secondary,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        zIndex: 3,
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        lineHeight: 1,
                        backgroundColor: flipped ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.9)',
                        px: 0.5,
                        borderRadius: 0.5,
                      }}
                    >
                      {patentSettings.accuracyThreshold}% Threshold
                    </Typography>
                  </Box>
                </Box>

                {/* Mastery Progress - 1/5 height (conditional) */}
                {getCurrentDisplayLevel() === 'verse' ? (
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 0,
                    px: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: flipped ? 'rgba(255,255,255,0.9)' : theme.palette.text.secondary }}
                      >
                        Mastery:
                      </Typography>
                      {[...Array(5)].map((_, i) => (
                        <CheckCircleIcon
                          key={i}
                          sx={{
                            color: i < mastery
                              ? (flipped ? '#FFB300' : theme.palette.warning.main)
                              : flipped ? 'rgba(255,255,255,0.3)' : theme.palette.action.disabled,
                            fontSize: 28,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1 }} />  /* Empty spacer when mastery hidden */
                )}

                {/* Microphone - 1/5 height */}
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 0
                }}>
                  <IconButton
                    onClick={toggleListening}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: isListening
                        ? (flipped ? 'rgba(255,255,255,0.9)' : '#ff5252')
                        : (flipped ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.04)'),
                      border: `3px solid ${isListening ? (flipped ? '#333' : '#d32f2f') : (flipped ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.23)')}`,
                      animation: isListening ? 'pulse 1.5s infinite' : 'none',
                      '&:hover': {
                        backgroundColor: isListening
                          ? (flipped ? 'rgba(255,255,255,0.95)' : '#ff6659')
                          : (flipped ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.08)'),
                      },
                      '& .MuiIconButton-icon': {
                        fontSize: '4rem',
                        color: isListening
                          ? (flipped ? '#333' : 'white')
                          : (flipped ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.54)'),
                      },
                    }}
                  >
                    {isListening ? <MicIcon /> : <MicOffIcon />}
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>

          {!flipped && (
            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', pt: { xs: 1, sm: 3 } }}>
              <Button
                variant="contained"
                onClick={handleFlip}
                startIcon={<FlipIcon />}
                sx={{
                  background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: theme.palette.primary.contrastText,
                  px: { xs: 2, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {getFlipButtonText()}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Verse Picker Drawer */}
      <Drawer
        anchor="bottom"
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            height: '60vh',
            borderRadius: '16px 16px 0 0',
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600 }}>
              Select Verse
            </Typography>
          </Box>

      {/* Picker Columns */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Books Column */}
        <Box sx={{ flex: 1, borderRight: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', color: theme.palette.text.secondary }}>
            Book
          </Typography>
          <List sx={{ overflow: 'auto', maxHeight: 'calc(100% - 50px)' }}>
            {books.map((book) => (
              <ListItem key={book.id} disablePadding>
                <ListItemButton
                  selected={book.id === selectedBookId}
                  onClick={() => handleBookSelect(book.id)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    color: theme.palette.text.primary,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  <ListItemText primary={book.name} sx={{ textAlign: 'center' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Chapters Column */}
        <Box sx={{ flex: 1, borderRight: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', color: theme.palette.text.secondary }}>
            Chapter
          </Typography>
          <List sx={{ overflow: 'auto', maxHeight: 'calc(100% - 50px)' }}>
            {availableChapters.map((chapter) => (
              <ListItem key={chapter.id} disablePadding>
                <ListItemButton
                  selected={chapter.number === selectedChapter}
                  onClick={() => handleChapterSelect(chapter.number)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    color: theme.palette.text.primary,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  <ListItemText primary={chapter.number} sx={{ textAlign: 'center' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Verses Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', color: theme.palette.text.secondary }}>
            Verse
          </Typography>
          <List sx={{ overflow: 'auto', maxHeight: 'calc(100% - 50px)' }}>
            {availableVerses.map((verse) => (
              <ListItem key={verse.id} disablePadding>
                <ListItemButton
                  selected={verse.number === selectedVerse}
                  onClick={() => handleVerseSelect(verse.number)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    color: theme.palette.text.primary,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      }}
                    >
                      <ListItemText primary={verse.number} sx={{ textAlign: 'center' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
          </List>
        </Box>
      </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, p: 2, pt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setIsPickerOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleConfirmPicker}
              sx={{ textTransform: 'none' }}
            >
              Select Verse
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Version Picker Drawer */}
      <Drawer
        anchor="bottom"
        open={isVersionDrawerOpen}
        onClose={() => setIsVersionDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            height: '50vh',
            borderRadius: '16px 16px 0 0',
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600 }}>
              Select Bible Version
            </Typography>
          </Box>

          {/* Bible Versions List */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
            <List>
              {bibles.map((bible) => (
                <ListItem key={bible.id} disablePadding>
                  <ListItemButton
                    selected={bible.id === selectedBible}
                    onClick={() => handleVersionSelect(bible)}
                    sx={{
                      py: 2,
                      px: 2,
                      backgroundColor: bible.id === selectedBible ? theme.palette.primary.main : 'transparent',
                      color: bible.id === selectedBible ? theme.palette.primary.contrastText : theme.palette.text.primary,
                      borderRadius: 1,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: bible.id === selectedBible ? theme.palette.primary.dark : theme.palette.action.hover
                      },
                    }}
                  >
                    <ListItemText
                      primary={bible.name}
                      primaryTypographyProps={{
                        variant: "body1",
                        fontWeight: bible.id === selectedBible ? 600 : 400,
                        textAlign: 'center'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Action Button */}
          <Box sx={{ p: 2, pt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setIsVersionDrawerOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        `}
      </style>
    </Box>
  );
};

export default Memorize;
